"use client";

import { useEffect, useState } from "react";
import { fetchGlobalDefaults, updateGlobalDefault } from "@/lib/queries";
import type { GlobalDefault } from "@/lib/types";
import { Header } from "@/app/components/Header";

const UNIT_SUFFIX: Record<GlobalDefault["unit"], string> = {
  percent: "%",
  dollars: "$",
  years: "yr",
  multiplier: "×",
  count: "",
};

export default function SettingsPage() {
  const [defaults, setDefaults] = useState<GlobalDefault[] | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchGlobalDefaults()
      .then((rows) => {
        setDefaults(rows);
        // Percent values are stored as fractions (0.10) but shown/edited
        // as whole percentages (10) for readability -- drafts hold the
        // DISPLAY value, converted back to a fraction on save.
        const initial: Record<string, string> = {};
        for (const row of rows) {
          initial[row.key] = row.unit === "percent" ? String(row.numeric_value * 100) : String(row.numeric_value);
        }
        setDrafts(initial);
      })
      .catch((e) => setError(e.message ?? String(e)));
  }, []);

  const handleSave = async (row: GlobalDefault) => {
    const draft = drafts[row.key];
    const parsed = Number(draft);
    if (Number.isNaN(parsed)) return;
    const storedValue = row.unit === "percent" ? parsed / 100 : parsed;

    setSaving(row.key);
    try {
      await updateGlobalDefault(row.key, storedValue);
      setDefaults((prev) =>
        prev ? prev.map((r) => (r.key === row.key ? { ...r, numeric_value: storedValue } : r)) : prev
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="mx-auto w-full max-w-3xl flex-1 px-6 py-8">
        <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">Global Defaults</h1>
        <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
          These values drive how listings are seeded and how projections are computed. Changes here
          take effect automatically on the next daily pipeline run — every Default Offer is reseeded
          from current defaults, then projections are recomputed.
        </p>

        {error && (
          <div
            className="mt-4 rounded-md border px-4 py-3 text-sm"
            style={{ borderColor: "var(--color-negative)", backgroundColor: "var(--color-negative-muted)", color: "var(--color-negative)" }}
          >
            {error}
          </div>
        )}

        {!defaults && !error && (
          <div className="py-16 text-center text-sm text-[var(--color-text-secondary)]">Loading…</div>
        )}

        {defaults && (
          <div className="mt-6 space-y-3">
            {defaults.map((row) => (
              <div
                key={row.key}
                className="flex items-center justify-between gap-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-1)] px-4 py-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-[var(--color-text-primary)]">{row.label}</div>
                  {row.description && (
                    <div className="mt-0.5 text-xs text-[var(--color-text-tertiary)]">{row.description}</div>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      step="any"
                      value={drafts[row.key] ?? ""}
                      onChange={(e) => setDrafts((prev) => ({ ...prev, [row.key]: e.target.value }))}
                      className="w-24 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-0)] px-2 py-1.5 text-right text-sm tabular-nums text-[var(--color-text-primary)] focus:border-[var(--color-accent)]"
                    />
                    <span className="text-xs text-[var(--color-text-tertiary)]">{UNIT_SUFFIX[row.unit]}</span>
                  </div>
                  <button
                    onClick={() => handleSave(row)}
                    disabled={saving === row.key}
                    className="rounded-md border px-3 py-1.5 text-xs font-medium disabled:opacity-50"
                    style={{ borderColor: "var(--color-accent)", color: "var(--color-accent-strong)" }}
                  >
                    {saving === row.key ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
